import { supabase } from '../lib/supabase';
import { getAuthError } from '../utils/errorHandler';
import { setAuthState, clearAuthState, getAuthState } from '../utils/auth';
import { getAvatarUrl } from '../utils/avatar';
// import config from '../config';

export interface ProfileData {
  id: string;
  email: string;
  full_name: string;
  whatsapp_number?: string;
  telegram_id?: string;
  avatar_url?: string;
  role: 'user' | 'admin' | 'developer' | 'pro';
  is_active: boolean;
  email_verified: boolean;
  last_login_at?: string;
  referred_by_code?: string;
  created_at: string;
  updated_at: string;
  // Legacy fields for backward compatibility
  name?: string;
  avatar?: string;
  avatarUrl?: string;
  joinedDate?: string;
  subscriptionPlan?: string;
  subscriptionStatus?: 'active' | 'inactive' | 'trial';
  subscriptionEndDate?: string;
  bootcamp_enrolled?: boolean;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  user?: any;
  session?: any;
  error?: string;
  code?: string;
  field?: string | undefined;
  message?: string;
  kicked_other_devices?: boolean;
}

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';

// Global 401 handler - emit event when 401 detected
const emit401Event = () => {
  console.log('🚨 API: 401 detected, emitting global-401 event');
  window.dispatchEvent(new CustomEvent('global-401'));
};

// Helper function to check response and handle 401
const checkResponse = async (response: Response, endpoint: string) => {
  if (response.status === 401) {
    console.log(`🚨 API: 401 from ${endpoint}`);
    emit401Event();

    // Try to parse error response to get specific error code
    let errorCode = 'UNAUTHORIZED';
    let errorMessage = 'Session expired. Please sign in again.';

    try {
      const errorData = await response.clone().json();
      errorCode = errorData.code || 'UNAUTHORIZED';
      errorMessage = errorData.error || 'Session expired. Please sign in again.';
    } catch (parseError) {
      // If can't parse JSON, use defaults (already set above)
      console.warn('API: Could not parse 401 error response, using generic error');
    }

    // Create error object with code property (always, even if parse failed)
    const error: any = new Error(errorMessage);
    error.code = errorCode;
    throw error;
  }
  return response;
};

// Wrapper function for authenticated fetch - automatically checks 401
const authenticatedFetch = async (
  url: string,
  options: RequestInit = {},
  endpoint?: string
): Promise<Response> => {
  // Get session and add Authorization header
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    console.log(`❌ API: No active session for authenticatedFetch to ${endpoint || url}`);
    // Emit 401 event to trigger global logout flow
    emit401Event();
    // Throw an error that ProfileContext can catch to force logout
    const error: any = new Error('No active session');
    error.code = 'NO_ACTIVE_SESSION';
    throw error;
  }

  const isFormData = options.body instanceof FormData;
  const existingHeaders = (options.headers || {}) as Record<string, string>;

  const headers: Record<string, string> = {
    ...existingHeaders,
    'Authorization': `Bearer ${session.access_token}`,
  };

  // Default to JSON if not FormData and no Content-Type is set
  if (!isFormData && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Always check for 401 on authenticated requests
  const endpointName = endpoint || url.replace(API_URL, '');
  await checkResponse(response, endpointName);
  return response;
};

// Helper for safe JSON parsing
const safeJson = async (res: Response) => {
  try {
    const text = await res.text();
    if (!text || text.trim() === '') return {};
    return JSON.parse(text);
  } catch (err) {
    console.warn('safeJson: Failed to parse response', err);
    return {};
  }
};

type SasTokenData = { containerUrl: string; sasToken: string; expiresAt: string };
type SasTokenResult = { success: boolean; data?: SasTokenData; error?: string };

// Cache for SAS token to avoid redundant backend calls
let sasTokenCache: SasTokenData | null = null;
let sasTokenPromise: Promise<SasTokenResult> | null = null;

// Cache for broker transaction SAS token to avoid redundant backend calls
let brokerTxSasTokenCache: SasTokenData | null = null;
let brokerTxSasTokenPromise: Promise<SasTokenResult> | null = null;

// Cache for broker summary SAS token to avoid redundant backend calls
let brokerSummarySasTokenCache: SasTokenData | null = null;
let brokerSummarySasTokenPromise: Promise<SasTokenResult> | null = null;

// Cache for stock metadata (watchlist/emiten list) SAS token to avoid redundant backend calls
let stockMetadataSasTokenCache: SasTokenData | null = null;
let stockMetadataSasTokenPromise: Promise<SasTokenResult> | null = null;


export const api = {
  // Market Rotation Outputs (Azure-backed)
  async listMarketRotationOutputs(feature: 'rrc' | 'rrg' | 'seasonal' | 'trend'): Promise<{ success: boolean; data?: { prefix: string; files: string[] }; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/market-rotation/outputs/${feature}/list`, {}, `/api/market-rotation/outputs/${feature}/list`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to list outputs');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to list outputs' };
    }
  },

  // Seasonal Analysis
  async getSeasonalityData(type: 'index' | 'sector' | 'stock', startDate?: string, endDate?: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const res = await authenticatedFetch(`${API_URL}/api/seasonality/data/${type}?${params}`, {}, `/api/seasonality/data/${type}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get seasonality data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get seasonality data' };
    }
  },

  async getSeasonalityStatus(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/seasonality/status`, {}, '/api/seasonality/status');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get seasonality status');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get seasonality status' };
    }
  },

  async generateSeasonality(triggerType: 'manual' | 'scheduled' = 'manual'): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/seasonality/generate`, {
        method: 'POST',
        body: JSON.stringify({ triggerType })
      }, '/api/seasonality/generate');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to generate seasonality');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to generate seasonality' };
    }
  },

  async getMarketRotationOutputFile(feature: 'rrc' | 'rrg' | 'seasonal' | 'trend', path: string): Promise<string> {
    const res = await authenticatedFetch(`${API_URL}/api/market-rotation/outputs/${feature}/file?path=${encodeURIComponent(path)}`, {}, `/api/market-rotation/outputs/${feature}/file`);
    if (!res.ok) {
      const err = await res.text();
      throw new Error(err || 'Failed to fetch output file');
    }
    return await res.text();
  },

  async backfillMarketRotationOutputs(feature: 'rrc' | 'rrg' | 'seasonal' | 'trend'): Promise<{ success: boolean; uploaded?: number; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/market-rotation/backfill/${feature}`, { method: 'POST' }, `/api/market-rotation/backfill/${feature}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to backfill outputs');
      return { success: true, uploaded: json?.data?.uploaded };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to backfill outputs' };
    }
  },
  async listMarketRotationInputs(): Promise<{ success: boolean; data?: { index: string[]; stockSectors: string[]; holding: string[]; shareholders: string[] }; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/market-rotation/inputs`, {}, '/api/market-rotation/inputs');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to list inputs');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to list inputs' };
    }
  },

  async preGenerateOutputs(feature: string): Promise<{ success: boolean; data?: { message: string; feature: string }; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/market-rotation/pre-generate/${feature}`, {
        method: 'POST',
      }, `/api/market-rotation/pre-generate/${feature}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to pre-generate outputs');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to pre-generate outputs' };
    }
  },

  async getStockMetadataSasToken(): Promise<SasTokenResult> {
    if (stockMetadataSasTokenCache) {
      const expiresTime = new Date(stockMetadataSasTokenCache.expiresAt).getTime();
      // If token is still valid for at least 5 minutes, use it
      if (expiresTime > Date.now() + 5 * 60 * 1000) {
        console.log('[API] Using cached stock metadata SAS token');
        return { success: true, data: stockMetadataSasTokenCache };
      }
    }
    // Reuse an in-flight request so concurrent callers don't trigger duplicate fetches
    if (stockMetadataSasTokenPromise) {
      return stockMetadataSasTokenPromise;
    }
    stockMetadataSasTokenPromise = (async (): Promise<SasTokenResult> => {
      try {
        const res = await authenticatedFetch(`${API_URL}/api/watchlist/sas-token`, {}, '/api/watchlist/sas-token');
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Failed to get SAS token');
        stockMetadataSasTokenCache = json.data;
        return { success: true, data: json.data };
      } catch (err: any) {
        return { success: false, error: err.message || 'Failed to get SAS token' };
      } finally {
        stockMetadataSasTokenPromise = null;
      }
    })();
    return stockMetadataSasTokenPromise;
  },

  async getOrderbookReplaySasToken(): Promise<SasTokenResult> {
    if (sasTokenCache) {
      const expiresTime = new Date(sasTokenCache.expiresAt).getTime();
      // If token is still valid for at least 5 minutes, use it
      if (expiresTime > Date.now() + 5 * 60 * 1000) {
        console.log('[API] Using cached SAS token');
        return { success: true, data: sasTokenCache };
      }
    }
    // Reuse an in-flight request so concurrent callers don't trigger duplicate fetches
    if (sasTokenPromise) {
      return sasTokenPromise;
    }
    sasTokenPromise = (async (): Promise<SasTokenResult> => {
      try {
        const res = await authenticatedFetch(`${API_URL}/api/orderbook-replay/sas-token`, {}, '/api/orderbook-replay/sas-token');
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Failed to get SAS token');
        sasTokenCache = json.data;
        return { success: true, data: json.data };
      } catch (err: any) {
        return { success: false, error: err.message || 'Failed to get SAS token' };
      } finally {
        sasTokenPromise = null;
      }
    })();
    return sasTokenPromise;
  },

  async getBrokerTransactionSasToken(): Promise<SasTokenResult> {
    if (brokerTxSasTokenCache) {
      const expiresTime = new Date(brokerTxSasTokenCache.expiresAt).getTime();
      // If token is still valid for at least 5 minutes, use it
      if (expiresTime > Date.now() + 5 * 60 * 1000) {
        console.log('[API] Using cached broker transaction SAS token');
        return { success: true, data: brokerTxSasTokenCache };
      }
    }
    // Reuse an in-flight request so concurrent callers don't trigger duplicate fetches
    if (brokerTxSasTokenPromise) {
      return brokerTxSasTokenPromise;
    }
    brokerTxSasTokenPromise = (async (): Promise<SasTokenResult> => {
      try {
        const res = await authenticatedFetch(`${API_URL}/api/broker/transaction/sas-token`, {}, '/api/broker/transaction/sas-token');
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Failed to get SAS token');
        brokerTxSasTokenCache = json.data;
        return { success: true, data: json.data };
      } catch (err: any) {
        return { success: false, error: err.message || 'Failed to get SAS token' };
      } finally {
        brokerTxSasTokenPromise = null;
      }
    })();
    return brokerTxSasTokenPromise;
  },

  async getBrokerSummarySasToken(): Promise<SasTokenResult> {
    if (brokerSummarySasTokenCache) {
      const expiresTime = new Date(brokerSummarySasTokenCache.expiresAt).getTime();
      // If token is still valid for at least 5 minutes, use it
      if (expiresTime > Date.now() + 5 * 60 * 1000) {
        console.log('[API] Using cached broker summary SAS token');
        return { success: true, data: brokerSummarySasTokenCache };
      }
    }
    // Reuse an in-flight request so concurrent callers don't trigger duplicate fetches
    if (brokerSummarySasTokenPromise) {
      return brokerSummarySasTokenPromise;
    }
    brokerSummarySasTokenPromise = (async (): Promise<SasTokenResult> => {
      try {
        const res = await authenticatedFetch(`${API_URL}/api/broker-summary/sas-token`, {}, '/api/broker-summary/sas-token');
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Failed to get SAS token');
        brokerSummarySasTokenCache = json.data;
        return { success: true, data: json.data };
      } catch (err: any) {
        return { success: false, error: err.message || 'Failed to get SAS token' };
      } finally {
        brokerSummarySasTokenPromise = null;
      }
    })();
    return brokerSummarySasTokenPromise;
  },


  async getOrderbookReplayLatestDate(): Promise<{ success: boolean; data?: { latestDate: string }; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/orderbook-replay/latest-date`, {}, '/api/orderbook-replay/latest-date');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get latest date');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get latest date' };
    }
  },

  async getProfile(): Promise<ProfileData> {
    console.log('🔍 API: Getting profile...');

    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.user) {
      console.log('❌ API: No active session');
      throw new Error('No active session');
    }

    if (!session.access_token) {
      console.log('❌ API: No access token');
      throw new Error('No access token');
    }

    console.log('✅ API: Session and token found, making request');

    // Add timeout to fetch
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 30000); // 30 seconds timeout

    try {
      const response = await authenticatedFetch(`${API_URL}/api/me`, {
        signal: controller.signal
      }, '/api/me');

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch profile: ${response.status} ${errorText}`);
      }

      const result = await response.json();

      if (result.ok && result.data) {
        const profile = result.data;
        const avatarUrl = getAvatarUrl(profile.avatar_url);
        const finalProfile = {
          ...profile,
          name: profile.full_name,
          avatar: avatarUrl,
          avatarUrl: avatarUrl,
          joinedDate: new Date(profile.created_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          subscriptionPlan: (profile.role === 'admin' || profile.role === 'developer')
            ? 'Pro'
            : (profile.subscription_plan || 'Free'),
          subscriptionStatus: (profile.role === 'admin' || profile.role === 'developer')
            ? ('active' as const)
            : (profile.subscription_status as 'active' | 'inactive' | 'trial' | undefined) || 'inactive',
          subscriptionEndDate: (profile.role === 'admin' || profile.role === 'developer')
            ? undefined
            : (profile.subscription_end_date || undefined), // Admin & Developer: No end date (active forever)
        };
        return finalProfile;
      }

      throw new Error('Invalid profile data');

    } catch (error: any) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        console.warn('⚠️ Profile fetch timeout, this might be a temporary issue');
        throw new Error('Request timeout');
      }

      if (error.message?.includes('Failed to fetch')) {
        throw new Error('Cannot connect to server. Please check your connection.');
      }

      throw error;
    }
  },

  async uploadAvatar(file: File): Promise<{ avatarUrl: string; filePath: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No active session');
      }

      // Pre-validate file size
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      console.log(`🔍 API: File size validation: ${file.size} bytes (${fileSizeMB}MB)`);

      const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
      if (file.size > MAX_FILE_SIZE) {
        console.log(`❌ API: File size validation failed: ${fileSizeMB}MB > ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
        throw new Error(`File size too large (${fileSizeMB}MB). Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
      }
      console.log(`✅ API: File size validation passed: ${fileSizeMB}MB <= ${MAX_FILE_SIZE / (1024 * 1024)}MB`);

      const formData = new FormData();
      formData.append('avatar', file);

      const response = await authenticatedFetch(
        `${API_URL}/api/auth/upload-avatar`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          },
          body: formData
        },
        '/api/auth/upload-avatar'
      );

      if (!response.ok) {
        let errorMessage = 'Failed to upload avatar';

        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response, use status-based messages
          if (response.status === 413) {
            errorMessage = 'File size too large. Maximum size is 1MB';
          } else if (response.status === 400) {
            errorMessage = 'Invalid file type or format';
          } else if (response.status === 401) {
            errorMessage = 'Authentication failed. Please log in again';
          } else if (response.status >= 500) {
            errorMessage = 'Server error. Please try again later';
          }
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      return result.data;
    } catch (error: any) {
      // Re-throw with more specific error messages
      if (error.message?.includes('File size too large')) {
        throw new Error('File size too large. Please select an image smaller than 1MB');
      } else if (error.message?.includes('Invalid file type')) {
        throw new Error('Invalid file type. Please select a JPEG, PNG, GIF, or WebP image');
      } else if (error.message?.includes('Failed to fetch')) {
        throw new Error('Network error. Please check your connection');
      }

      throw error;
    }
  },

  async deleteAvatar(): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No active session');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/auth/avatar`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        },
        '/api/auth/avatar'
      );

      if (!response.ok) {
        let errorMessage = 'Failed to delete avatar';

        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response, use status-based messages
          if (response.status === 404) {
            errorMessage = 'No avatar found to delete';
          } else if (response.status === 401) {
            errorMessage = 'Authentication failed. Please log in again';
          } else if (response.status >= 500) {
            errorMessage = 'Server error. Please try again later';
          }
        }

        throw new Error(errorMessage);
      }
    } catch (error: any) {
      // Re-throw with more specific error messages
      if (error.message?.includes('Failed to fetch')) {
        throw new Error('Network error. Please check your connection');
      }

      throw error;
    }
  },

  // Session Management
  async getSessions(): Promise<{ sessions: any[]; count: number }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No active session');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/auth/sessions`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        },
        '/api/auth/sessions'
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get sessions');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      throw error;
    }
  },

  async deactivateSession(sessionId: string): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No active session');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/auth/sessions/${sessionId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        },
        '/api/auth/sessions/:id'
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to deactivate session');
      }
    } catch (error) {
      throw error;
    }
  },

  async deactivateAllSessions(): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No active session');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/auth/sessions/all`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        },
        '/api/auth/sessions/all'
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to deactivate all sessions');
      }
    } catch (error) {
      throw error;
    }
  },

  async updateProfile(data: Partial<ProfileData>): Promise<ProfileData> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No active session');
      }


      const response = await authenticatedFetch(
        `${API_URL}/api/me`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        },
        '/api/me'
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update profile');
      }

      const result = await response.json();
      console.log('📥 API: Received response:', result);

      if (result.ok && result.data) {
        const profile = result.data;
        const avatarUrl = getAvatarUrl(profile.avatar_url);
        const processedProfile = {
          ...profile,
          // Legacy fields for backward compatibility
          name: profile.full_name,
          avatar: avatarUrl,
          avatarUrl: avatarUrl,
          joinedDate: new Date(profile.created_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          subscriptionPlan: (profile.role === 'admin' || profile.role === 'developer')
            ? 'Pro'
            : (profile.subscription_plan || 'Free'),
          subscriptionStatus: (profile.role === 'admin' || profile.role === 'developer')
            ? ('active' as const)
            : (profile.subscription_status as 'active' | 'inactive' | 'trial' | undefined) || 'inactive',
          subscriptionEndDate: (profile.role === 'admin' || profile.role === 'developer')
            ? undefined
            : (profile.subscription_end_date || undefined), // Admin & Developer: No end date (active forever)
        };
        console.log('✅ API: Profile processed successfully');
        return processedProfile;
      }

      console.log('❌ API: Invalid profile data received');
      throw new Error('Invalid profile data');
    } catch (error) {
      console.error('❌ API: Error in getProfile:', error);
      throw error;
    }
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No active session');
      }

      console.log('📤 API: Changing password...');
      const response = await authenticatedFetch(
        `${API_URL}/api/auth/change-password`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ currentPassword, newPassword })
        },
        '/api/auth/change-password'
      );

      const result = await response.json();

      if (!response.ok) {
        console.error('❌ API: Change password failed:', result);
        throw new Error(result.error || result.message || 'Failed to change password');
      }

      console.log('✅ API: Password changed successfully');

      // After successful password change, we need to refresh the session
      // This is important because Supabase might issue a new token
      const { data: { session: newSession }, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError) {
        console.warn('⚠️ API: Session refresh after password change failed:', refreshError);
        // Don't throw - password was changed successfully, just session refresh failed
      } else if (newSession) {
        console.log('✅ API: Session refreshed successfully after password change');
      }

      return { success: true, message: result.message || 'Password changed successfully' };
    } catch (error: any) {
      console.error('❌ API: changePassword error:', error);
      throw error;
    }
  },

  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      // Use backend login to ensure session is created in database
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle structured error responses from backend
        return {
          success: false,
          error: result.error || 'Login failed',
          code: result.code,
          field: result.field
        };
      }

      if (result.ok && result.data) {
        // Set session in Supabase client first
        if (result.data.session) {
          await supabase.auth.setSession({
            access_token: result.data.session.access_token,
            refresh_token: result.data.session.refresh_token
          });
          // Then store in localStorage for persistence
          setAuthState(result.data.user, result.data.session);
        }

        return {
          success: true,
          token: result.data.session?.access_token,
          user: result.data.user,
          session: result.data.session,
          message: result.message,
          kicked_other_devices: result.data.kicked_other_devices
        };
      }

      return { success: false, error: 'Login failed' };
    } catch (error: any) {
      // Handle connection errors
      if (error.message?.includes('Failed to fetch') || error.message?.includes('ERR_CONNECTION_REFUSED')) {
        return {
          success: false,
          error: 'Cannot connect to server. Please make sure the backend is running.',
          code: 'CONNECTION_ERROR',
          field: 'general'
        } as AuthResponse;
      }

      const authError = getAuthError(error);
      return {
        success: false,
        error: authError.message,
        code: authError.code,
        field: authError.field
      } as AuthResponse;
    }
  },

  async register(name: string, email: string, password: string, whatsapp_number: string, telegram_id: string, referral_code?: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          full_name: name, 
          email, 
          password,
          whatsapp_number,
          telegram_id,
          referred_by_code: referral_code || null 
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle structured error responses from backend
        return {
          success: false,
          error: result.error || 'Registration failed',
          code: result.code,
          field: result.field
        };
      }

      if (result.ok && result.data) {
        // Store session in localStorage for persistence
        if (result.data.session) {
          setAuthState(result.data.user, result.data.session);
        }

        return {
          success: true,
          token: result.data.session?.access_token,
          user: result.data.user,
          session: result.data.session,
          message: result.message
        };
      }

      return { success: true, error: 'Please check your email to verify your account' };
    } catch (error: any) {
      const authError = getAuthError(error);
      return {
        success: false,
        error: authError.message,
        code: authError.code,
        field: authError.field
      };
    }
  },

  async logout(): Promise<void> {
    try {
      // Call backend logout route first (session-specific logout)
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          await fetch(`${API_URL}/api/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
          });
        }
      } catch (backendError) {
        console.warn('Backend logout failed:', backendError);
        // Continue with frontend logout even if backend fails
      }

      // Clear ALL auth-related storage (including all sb-* keys)
      // DO: Clear storage BEFORE reload, DON'T call signOut() (can re-create session)
      clearAuthState();

      // Use window.location.replace for clean reload (don't call signOut())
      // This prevents Supabase from re-creating session from memory cache
      // The reload will ensure completely clean state
      // Note: AuthContext will handle the redirect if needed
      console.log('API: Storage cleared, reload will be handled by AuthContext or redirect');
    } catch (error) {
      console.error('Logout error:', error);
      // Clear local storage anyway on error
      clearAuthState();
    }
  },

  async logoutAllDevices(): Promise<void> {
    try {
      // Call backend logout-all route first
      try {
        await fetch(`${API_URL}/api/auth/logout-all`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json',
          },
        });
      } catch (backendError) {
        // Continue with frontend logout even if backend fails
      }

      // Clear ALL auth-related storage (including all sb-* keys)
      // DO: Clear storage BEFORE reload, DON'T call signOut() (can re-create session)
      clearAuthState();

      // Use window.location.replace for clean reload (don't call signOut())
      // This prevents Supabase from re-creating session from memory cache
      // The reload will ensure completely clean state
      // Note: AuthContext will handle the redirect if needed
      console.log('API: Storage cleared for all devices, reload will be handled by AuthContext or redirect');
    } catch (error) {
      console.error('Logout all devices error:', error);
      // Clear local storage anyway on error
      clearAuthState();
    }
  },

  async forgotPassword(email: string): Promise<{ success: boolean; error?: string; message?: string }> {
    try {
      // Use backend API to check email exists and send reset email
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle error response from backend (404 for user not found, etc.)
        const errorMessage = result.error || 'Gagal mengirim link reset password';
        return {
          success: false,
          error: errorMessage
        };
      }

      // Success response
      return {
        success: true,
        message: result.message || 'Email berhasil dikirim, silahkan cek akun Anda'
      };
    } catch (error: any) {
      console.error('Forgot password error:', error);
      return {
        success: false,
        error: error.message || 'Gagal mengirim link reset password'
      };
    }
  },

  async verifyEmail(token: string, type: 'signup' | 'recovery' = 'signup'): Promise<AuthResponse> {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, type })
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || 'Email verification failed',
          code: result.code,
          field: result.field
        };
      }

      if (result.ok && result.data) {
        // Store session in localStorage for persistence
        if (result.data.session) {
          setAuthState(result.data.user, result.data.session);
        }

        return {
          success: true,
          token: result.data.session?.access_token,
          user: result.data.user,
          session: result.data.session,
          message: result.message
        };
      }

      return { success: false, error: 'Email verification failed' };
    } catch (error: any) {
      return { success: false, error: error.message || 'Email verification failed' };
    }
  },

  async resendVerification(email: string): Promise<{ success: boolean; error?: string; message?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || 'Failed to resend verification email',
          message: result.message
        };
      }

      return {
        success: true,
        message: result.message || 'Verification email sent successfully'
      };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to resend verification email' };
    }
  },

  async resendForgotPassword(email: string): Promise<{ success: boolean; error?: string; message?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || 'Failed to resend password reset email',
          message: result.message
        };
      }

      return {
        success: true,
        message: result.message || 'Password reset email sent successfully'
      };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to resend password reset email' };
    }
  },


  isAuthenticated(): boolean {
    const authState = getAuthState();
    return authState.isAuthenticated;
  },

  async getCurrentUser() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  },

  async checkEmail(email: string): Promise<{ success: boolean; exists?: boolean; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/auth/check-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        throw new Error('Failed to check email');
      }

      const result = await response.json();

      if (result.ok) {
        return { success: true, exists: result.data.exists };
      }

      return { success: false, error: result.error };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to check email' };
    }
  },

  async checkAttempts(email: string): Promise<{
    success: boolean;
    data?: {
      allowed: boolean;
      remainingAttempts: number;
      blockedUntil?: string;
      isSuspended: boolean;
      suspensionReason?: string;
      timeRemaining?: string;
    };
    error?: string;
  }> {
    try {
      const response = await fetch(`${API_URL}/api/auth/check-attempts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to check attempts');
      }

      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to check attempts' };
    }
  },

  async verifyOTP(email: string, otp: string): Promise<{ success: boolean; error?: string; token?: string }> {
    try {
      console.log('API: Verifying OTP for email:', email);

      // Use Supabase OTP verification for reauthentication
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'email'
      });

      if (error) {
        throw new Error(error.message || 'OTP verification failed');
      }

      const tokenValue = (data.session?.access_token ?? undefined) as string | undefined;
      return { success: true, token: tokenValue } as { success: boolean; error?: string; token?: string };
    } catch (error: any) {
      return { success: false, error: error.message || 'OTP verification failed' };
    }
  },

  async resetPassword(newPassword: string): Promise<{ ok: boolean; error?: string }> {
    try {
      // Get current session to get the token
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No active session found');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/auth/reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ password: newPassword })
        },
        '/api/auth/reset-password'
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to reset password');
      }

      return { ok: true };
    } catch (error: any) {
      return { ok: false, error: error.message || 'Failed to reset password' };
    }
  },

  // Subscription API methods
  async getSubscriptionPlans(includeInactive: boolean = false): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const url = new URL(`${API_URL}/api/subscription/plans`);
      if (includeInactive) {
        url.searchParams.append('include_inactive', 'true');
      }
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to get subscription plans');
      }

      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to get subscription plans' };
    }
  },

  async getPaymentMethods(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/subscription/payment-methods`, {}, '/api/subscription/payment-methods');
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to get payment methods');
      }

      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to get payment methods' };
    }
  },

  async createSubscriptionOrder(data: {
    planId: string;
    paymentMethod: string;
    referralCode?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No active session found');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/subscription/create-order`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify(data)
        },
        '/api/subscription/create-order'
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create subscription order');
      }

      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to create subscription order' };
    }
  },

  async getSubscriptionStatus(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No active session found');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/subscription/status`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        },
        '/api/subscription/status'
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to get subscription status');
      }

      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to get subscription status' };
    }
  },

  async startTrial(): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new Error('No active session');
    }

    const response = await authenticatedFetch(
      `${API_URL}/api/subscription/start-trial`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      },
      '/api/subscription/start-trial'
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to start trial');
    }

    const result = await response.json();
    if (!result.ok) {
      throw new Error(result.error || 'Failed to start trial');
    }
  },

  async getTrialStatus(): Promise<{ eligible: boolean; hasActiveTrial: boolean; trial?: any; durationDays?: number }> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new Error('No active session');
    }

    const response = await authenticatedFetch(
      `${API_URL}/api/subscription/trial-status`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      },
      '/api/subscription/trial-status'
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to get trial status');
    }

    const result = await response.json();
    if (!result.ok) {
      throw new Error(result.error || 'Failed to get trial status');
    }

    return result.data;
  },

  async cancelSubscription(data: {
    reason?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No active session found');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/subscription/cancel`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify(data)
        },
        '/api/subscription/cancel'
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to cancel subscription');
      }

      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to cancel subscription' };
    }
  },

  async getPaymentActivity(params?: {
    limit?: number;
    offset?: number;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No active session found');
      }

      const queryParams = new URLSearchParams();
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.offset) queryParams.append('offset', params.offset.toString());

      const response = await authenticatedFetch(
        `${API_URL}/api/subscription/payment-activity?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        },
        '/api/subscription/payment-activity'
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to get payment activity');
      }

      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to get payment activity' };
    }
  },

  async checkPaymentStatus(orderId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No active session found');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/subscription/check-status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ orderId })
        },
        '/api/subscription/check-status'
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to check payment status');
      }

      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to check payment status' };
    }
  },

  async regenerateSnapToken(data: {
    transactionId: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No active session found');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/subscription/regenerate-snap-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify(data)
        },
        '/api/subscription/regenerate-snap-token'
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to regenerate snap token');
      }

      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to regenerate snap token' };
    }
  },

  async cancelPendingTransaction(data: {
    transactionId: string;
    reason?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No active session found');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/subscription/cancel-pending`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify(data)
        },
        '/api/subscription/cancel-pending'
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to cancel pending transaction');
      }

      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to cancel pending transaction' };
    }
  },

  async updatePaymentMethod(params: {
    transactionId: string;
    paymentMethod: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('No active session found');
      }

      const response = await authenticatedFetch(
        `${API_URL}/api/subscription/update-payment-method`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify(params)
        },
        '/api/subscription/update-payment-method'
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update payment method');
      }

      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to update payment method' };
    }
  },

  // RRC API methods
  async listRRCInputs(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/rrc/inputs`, {}, '/api/rrc/inputs');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to list RRC inputs');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to list RRC inputs' };
    }
  },

  async getRRCStatus(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/rrc/status`, {}, '/api/rrc/status');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get RRC status');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get RRC status' };
    }
  },

  // Debug API methods
  async triggerRRCUpdate(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/rrc/debug/trigger-update`, {
        method: 'POST',
      }, '/api/rrc/debug/trigger-update');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to trigger RRC update');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to trigger RRC update' };
    }
  },

  // Unified trigger with options
  async triggerGeneration(feature: 'rrc' | 'rrg' | 'seasonal' | 'all' = 'all'): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/trigger/generate?feature=${feature}`, {
        method: 'POST',
      }, '/api/trigger/generate');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to trigger generation');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to trigger generation' };
    }
  },

  async getGenerationStatus(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/trigger/status`, {}, '/api/trigger/status');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get status');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get status' };
    }
  },

  async stopRRCGeneration(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/rrc/debug/stop-generation`, {
        method: 'POST',
      }, '/api/rrc/debug/stop-generation');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to stop RRC generation');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to stop RRC generation' };
    }
  },

  async clearRRCCache(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/rrc/debug/clear-cache`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to clear RRC cache');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to clear RRC cache' };
    }
  },

  // RRG API methods
  async listRRGInputs(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/rrg/inputs`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to list RRG inputs');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to list RRG inputs' };
    }
  },

  async getRRGData(type: 'stock' | 'sector' | 'index', items: string[], index: string = 'COMPOSITE', startDate?: string, endDate?: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const params = new URLSearchParams();
      params.append('type', type);
      items.forEach(item => params.append('items', item));
      params.append('index', index);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const res = await fetch(`${API_URL}/api/rrg/data?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get RRG data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get RRG data' };
    }
  },

  async getRRGStatus(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/rrg/status`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get RRG status');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get RRG status' };
    }
  },

  // Debug RRG API methods
  async triggerRRGUpdate(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/rrg/debug/trigger-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to trigger RRG update');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to trigger RRG update' };
    }
  },

  async stopRRGGeneration(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/rrg/debug/stop-generation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to stop RRG generation');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to stop RRG generation' };
    }
  },

  async clearRRGCache(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/rrg/debug/clear-cache`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to clear RRG cache');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to clear RRG cache' };
    }
  },

  // RRG Scanner API
  async getRRGScannerData(type: 'stock' | 'sector'): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/rrg/scanner/${type}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get RRG scanner data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get RRG scanner data' };
    }
  },

  // Seasonality API
  async getSeasonalityInputs(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/seasonality/inputs`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get seasonality inputs');
      return { success: true, data: json };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get seasonality inputs' };
    }
  },

  async clearSeasonalityCache(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/seasonality/debug/clear-cache`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to clear seasonality cache');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to clear seasonality cache' };
    }
  },

  async getRRCData(type: 'stock' | 'sector' | 'index', items: string[], index: string = 'COMPOSITE'): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const params = new URLSearchParams();
      params.append('type', type);
      items.forEach(item => params.append('items', item));
      params.append('index', index);

      const res = await fetch(`${API_URL}/api/rrc/data?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get RRC data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get RRC data' };
    }
  },

  async preGenerateRRC(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/rrc/pre-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to pre-generate RRC');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to pre-generate RRC' };
    }
  },

  // Trend Filter API
  async getTrendFilterStatus(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/trend-filter/status`);
      const json = await safeJson(res);
      if (!res.ok) throw new Error(json.error || 'Failed to get trend filter status');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get trend filter status' };
    }
  },

  async generateTrendFilter(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/trend-filter/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await safeJson(res);
      if (!res.ok) throw new Error(json.error || 'Failed to generate trend filter');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to generate trend filter' };
    }
  },

  async getTrendFilterData(period?: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const url = period
        ? `${API_URL}/api/trend-filter/data/${period}`
        : `${API_URL}/api/trend-filter/data`;
      const res = await authenticatedFetch(url);
      const json = await safeJson(res);
      if (!res.ok) throw new Error(json.error || 'Failed to get trend filter data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get trend filter data' };
    }
  },

  async getTrendFilterPeriods(): Promise<{ success: boolean; data?: string[]; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/trend-filter/periods`);
      const json = await safeJson(res);
      if (!res.ok) throw new Error(json.error || 'Failed to get trend filter periods');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get trend filter periods' };
    }
  },

  // Money Flow API
  async getMoneyFlowData(stockCode: string, limit?: number): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());

      const res = await fetch(`${API_URL}/api/moneyflow/stock/${stockCode}?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get money flow data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get money flow data' };
    }
  },
  // Holding/Shareholding API
  async getHoldingData(stockCode: string, limit?: number): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());

      const res = await fetch(`${API_URL}/api/holding/stock/${stockCode}?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get holding data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get holding data' };
    }
  },

  // Shareholders API
  async getShareholdersData(stockCode: string, limit?: number): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());

      const res = await fetch(`${API_URL}/api/shareholders/stock/${stockCode}?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get shareholders data');
      return { success: true, data: json };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get shareholders data' };
    }
  },

  // Get list of stocks from shareholders directory
  async getShareholdersStockList(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/shareholders/list`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get shareholders stock list');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get shareholders stock list' };
    }
  },

  // Stock Data API
  async getStockList(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/stock/list`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get stock list');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get stock list' };
    }
  },

  async getStockData(stockCode: string, startDate?: string, endDate?: string, limit?: number): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (limit) params.append('limit', limit.toString());

      const res = await fetch(`${API_URL}/api/stock/data/${stockCode}?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get stock data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get stock data' };
    }
  },

  // Get sector OHLC price data
  async getSectorOhlcPrice(sectorName: string, startDate?: string, endDate?: string, limit?: number): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (limit) params.append('limit', limit.toString());

      const res = await fetch(`${API_URL}/api/sector-ohlc-price/${encodeURIComponent(sectorName)}?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get sector OHLC price');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get sector OHLC price' };
    }
  },

  // Get bid/ask data for specific stock and date
  async getBidAskData(stockCode: string, date: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/bidask/stock/${stockCode}/${date}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get bid/ask data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get bid/ask data' };
    }
  },

  // Get Buy/Sell Frequency data for specific stock and date
  async getBuySellFrequencyData(stockCode: string, date: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/bidask/frequency/${stockCode}/${date}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get Buy/Sell Frequency data');
      console.log(`📊 API getBuySellFrequencyData response for ${stockCode} on ${date}:`, {
        success: json.success,
        dataExists: !!json.data,
        dataDataExists: !!json.data?.data,
        dataLength: json.data?.data?.length || 0,
        sampleData: json.data?.data?.slice(0, 2) || []
      });
      return { success: true, data: json.data };
    } catch (err: any) {
      console.error(`❌ API getBuySellFrequencyData error for ${stockCode} on ${date}:`, err);
      return { success: false, error: err.message || 'Failed to get Buy/Sell Frequency data' };
    }
  },


  // Get broker list (backend reads raw/brokermetadata.parquet)
  async getBrokerList(): Promise<{ success: boolean; data?: { brokers: string[] }; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/broker/list`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get broker list');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get broker list' };
    }
  },

  // Broker Transaction Data
  async getBrokerTransactionData(code: string, date: string, pivot?: 'Broker' | 'Stock', inv?: 'F' | 'D' | '', board?: 'RG' | 'TN' | 'NG' | ''): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      // Convert YYYY-MM-DD to YYYYMMDD format
      const dateStr = date.includes('-') ? date.replace(/-/g, '') : date;
      const params = new URLSearchParams();
      params.append('date', dateStr);
      if (pivot) {
        params.append('pivot', pivot);
      }
      // Always append inv and board parameters, even if empty, to ensure backend receives correct values
      // Empty string means "All" for inv and "All Trade" for board
      params.append('inv', inv || '');
      params.append('board', board || '');
      // CRITICAL: Encode code to handle sector names with spaces (e.g., "Basic Materials_ALL" -> "Basic%20Materials_ALL")
      // Backend will decode this to get the actual sector name
      const encodedCode = encodeURIComponent(code);
      const res = await fetch(`${API_URL}/api/broker/transaction/${encodedCode}?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get broker transaction data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get broker transaction data' };
    }
  },


  async getMultipleStocksData(stockCodes: string[], startDate?: string, endDate?: string, limit?: number): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const params = new URLSearchParams();
      stockCodes.forEach(code => params.append('stocks', code));
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (limit) params.append('limit', limit.toString());

      const res = await fetch(`${API_URL}/api/stock/data?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get stocks data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get stocks data' };
    }
  },

  // Footprint Data API
  async getFootprintData(stockCode: string, date: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/stock/footprint/${stockCode}?date=${date}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get footprint data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get footprint data' };
    }
  },

  // Done Summary Data API
  async getDoneSummaryData(stockCode: string, date: string, limit?: number): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());

      const res = await fetch(`${API_URL}/api/done-summary/stock/${stockCode}/${date}?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get done summary data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get done summary data' };
    }
  },

  // Get list of available dates from done-summary
  async getDoneSummaryDates(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/done-summary/dates`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get done summary dates');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get done summary dates' };
    }
  },

  // Get list of available stocks from done-summary
  async getDoneSummaryStocks(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/done-summary/stocks`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get done summary stocks');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get done summary stocks' };
    }
  },

  // Get broker breakdown data for specific stock and date
  async getBrokerBreakdownData(stockCode: string, date: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/done-summary/broker-breakdown/${stockCode}/${date}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get broker breakdown data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get broker breakdown data' };
    }
  },

  // Batch get broker breakdown data for multiple dates
  // Get broker breakdown data for done summary with broker/fd/board parameters
  async getDoneSummaryBrokerBreakdown(stockCode: string, date: string, broker?: string, fd?: string, board?: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const params = new URLSearchParams();
      if (broker) params.append('broker', broker);
      if (fd) params.append('fd', fd);
      // Always append board parameter, even if empty string
      // Empty string means "All Boards" (files without board suffix in blob storage)
      params.append('board', board || '');

      const res = await fetch(`${API_URL}/api/broker-breakdown/done-summary/${stockCode}/${date}?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get done summary broker breakdown data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get done summary broker breakdown data' };
    }
  },

  async getBrokerBreakdownBatch(stockCode: string, dates: string[]): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const promises = dates.map(date => this.getBrokerBreakdownData(stockCode, date));
      const results = await Promise.allSettled(promises);

      const dataByDate: { [date: string]: any } = {};
      let successCount = 0;

      results.forEach((result, index) => {
        const date = dates[index];
        if (date) {
          if (result.status === 'fulfilled' && result.value.success) {
            dataByDate[date] = result.value.data;
            successCount++;
          } else {
            dataByDate[date] = { brokerBreakdownData: [], total: 0 };
          }
        }
      });

      return {
        success: successCount > 0,
        data: {
          dataByDate,
          successCount,
          totalDates: dates.length
        }
      };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get batch broker breakdown data' };
    }
  },

  // Batch get done summary data for multiple dates - much faster
  async getDoneSummaryBatch(stockCode: string, dates: string[]): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const datesParam = dates.join(',');
      const res = await fetch(`${API_URL}/api/done-summary/batch/${stockCode}?dates=${datesParam}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get batch done summary data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get batch done summary data' };
    }
  },

  // Pagination for large datasets
  async getDoneSummaryPagination(stockCode: string, date: string, page: number = 1, pageSize: number = 1000, limit: number = 10000): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/done-summary/pagination/${stockCode}/${date}?page=${page}&pageSize=${pageSize}&limit=${limit}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get paginated done summary data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get paginated done summary data' };
    }
  },

  // ===== BREAK DONE TRADE API =====

  // Get list of available dates from done_detail directory
  async getBreakDoneTradeDates(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/break-done-trade/dates`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get break done trade dates');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get break done trade dates' };
    }
  },

  // Get list of available stocks for a specific date from done_detail directory
  async getBreakDoneTradeStocks(date: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/break-done-trade/stocks/${date}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get break done trade stocks');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get break done trade stocks' };
    }
  },

  // Get break done trade data for specific stock and date
  async getBreakDoneTradeData(stockCode: string, date: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/break-done-trade/data/${date}/${stockCode}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get break done trade data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get break done trade data' };
    }
  },

  // Batch get break done trade data for multiple dates
  async getBreakDoneTradeBatch(stockCode: string, dates: string[]): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const promises = dates.map(date => this.getBreakDoneTradeData(stockCode, date));
      const results = await Promise.allSettled(promises);

      const dataByDate: { [date: string]: any } = {};
      let successCount = 0;

      results.forEach((result, index) => {
        const date = dates[index];
        if (date) {
          if (result.status === 'fulfilled' && result.value.success) {
            dataByDate[date] = result.value.data;
            successCount++;
          } else {
            dataByDate[date] = { doneTradeData: [], total: 0 };
          }
        }
      });

      return {
        success: successCount > 0,
        data: {
          dataByDate,
          successCount,
          totalDates: dates.length
        }
      };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get batch break done trade data' };
    }
  },

  // Get pivot data for break done trade
  async getBreakDoneTradePivot(
    stockCode: string,
    dates: string[],
    pivotType: string
  ): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/break-done-trade/pivot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stockCode,
          dates,
          pivotType,
        }),
      });

      const result = await response.json();
      return result;
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get pivot data' };
    }
  },

  // ===== ACCUMULATION DISTRIBUTION API =====

  // Calculate and store accumulation distribution data for specific date
  async calculateAccumulationDistribution(date: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/accumulation/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to calculate accumulation distribution data');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to calculate accumulation distribution data' };
    }
  },


  async getBidAskDates(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/bidask/dates`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get bid/ask dates');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get bid/ask dates' };
    }
  },

  async getBidAskStocks(date: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await fetch(`${API_URL}/api/bidask/stocks/${date}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get bid/ask stocks');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get bid/ask stocks' };
    }
  },

  // Batch get bid/ask data for multiple dates
  async getBidAskBatch(stockCode: string, dates: string[]): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const promises = dates.map(date => this.getBidAskData(stockCode, date));
      const results = await Promise.allSettled(promises);

      const dataByDate: { [date: string]: any } = {};
      let successCount = 0;

      results.forEach((result, index) => {
        const date = dates[index];
        if (date) {
          if (result.status === 'fulfilled' && result.value.success) {
            dataByDate[date] = result.value.data;
            successCount++;
          } else {
            dataByDate[date] = { data: [], total: 0 };
          }
        }
      });

      return {
        success: successCount > 0,
        data: {
          dataByDate,
          successCount,
          totalDates: dates.length
        }
      };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get batch bid/ask data' };
    }
  },

  // Broker Summary API
  getBrokerSummaryData: async (stockCode: string, date: string, market: 'RG' | 'TN' | 'NG' | '' = '', investorType: 'D' | 'F' | '' = '') => {
    try {
      // Convert YYYY-MM-DD to YYYYMMDD format
      const dateStr = date.includes('-') ? date.replace(/-/g, '') : date;
      // When market is empty string (All Trade), send it as empty string
      const marketParam = market || '';
      const investorParam = investorType || '';

      // Build URL with optional parameters
      let url = `${API_URL}/api/broker-summary/summary/${stockCode}?date=${dateStr}`;
      if (marketParam) url += `&market=${marketParam}`;
      if (investorParam) url += `&investorType=${investorParam}`;

      console.log(`[API] Fetching broker summary: ${url}`);

      const response = await authenticatedFetch(url, {}, 'getBrokerSummaryData');
      const data = await response.json();

      if (!response.ok) {
        console.error(`[API] Broker summary request failed: ${response.status}`, data);
        return { success: false, error: data.error || `HTTP ${response.status}: Failed to get broker summary data` };
      }

      console.log(`[API] Broker summary response:`, { success: data.success, hasData: !!data.data, hasBrokerData: !!data.data?.brokerData, brokerDataLength: data.data?.brokerData?.length || 0 });
      return data;
    } catch (err: any) {
      console.error(`[API] Broker summary fetch error:`, err);
      return { success: false, error: err.message || 'Failed to get broker summary data' };
    }
  },

  // Get available dates for broker summary
  getBrokerSummaryDates: async () => {
    try {
      // Increase timeout to 60 seconds for Azure Blob Storage operations
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

      const response = await authenticatedFetch(`${API_URL}/api/broker/dates`, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
        },
      }, 'getBrokerSummaryDates');

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[API] getBrokerSummaryDates error response:', response.status, errorText);
        return {
          success: false,
          error: `HTTP ${response.status}: ${errorText || 'Failed to get broker summary dates'}`
        };
      }

      const data = await response.json();
      console.log('[API] getBrokerSummaryDates success:', data);
      return data;
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.error('[API] getBrokerSummaryDates timeout after 60 seconds');
        return { success: false, error: 'Request timeout - Azure Blob Storage operation took too long' };
      }
      console.error('[API] getBrokerSummaryDates error:', err);
      return { success: false, error: err.message || 'Failed to get broker summary dates' };
    }
  },

  // NEW: F/D Specific Broker Summary API
  getBrokerSummaryFDData: async (stockCode: string, date: string, market: 'RG' | 'TN' | 'NG' | '' = '', investorType: 'D' | 'F') => {
    try {
      const dateStr = date.includes('-') ? date.replace(/-/g, '') : date;
      const marketParam = market || 'RG';
      const url = `${API_URL}/api/broker-summary/fd/summary/${stockCode}?date=${dateStr}&market=${marketParam}&investorType=${investorType}`;

      console.log(`[API] Fetching broker summary F/D: ${url}`);

      const response = await authenticatedFetch(url, {}, 'getBrokerSummaryFDData');
      const data = await response.json();

      if (!response.ok) {
        console.error(`[API] Broker summary F/D request failed: ${response.status}`, data);
        return { success: false, error: data.error || `HTTP ${response.status}: Failed to get broker summary F/D data` };
      }

      return data;
    } catch (err: any) {
      console.error(`[API] Broker summary F/D fetch error:`, err);
      return { success: false, error: err.message || 'Failed to get broker summary F/D data' };
    }
  },

  getBrokerSummaryFDStocks: async (dateOrDates: string | string[], market: 'RG' | 'TN' | 'NG' | '' = '', investorType: 'D' | 'F') => {
    try {
      let params = '';
      if (Array.isArray(dateOrDates)) {
        const dateStr = dateOrDates.map(d => d.includes('-') ? d.replace(/-/g, '') : d).join(',');
        params = `dates=${dateStr}`;
      } else {
        const dateStr = dateOrDates.includes('-') ? dateOrDates.replace(/-/g, '') : dateOrDates;
        params = `date=${dateStr}`;
      }

      const marketParam = market || 'RG';
      const url = `${API_URL}/api/broker-summary/fd/stocks?${params}&market=${marketParam}&investorType=${investorType}`;

      const response = await authenticatedFetch(url, {}, 'getBrokerSummaryFDStocks');
      const data = await response.json();
      return data;
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get broker summary F/D stocks' };
    }
  },

  // Get broker inventory data (cumulative net flow)
  getBrokerInventoryData: async (stockCode: string, brokerCode: string) => {
    try {
      const url = `${API_URL}/api/broker-inventory/${stockCode}/${brokerCode}`;
      console.log(`[API] Fetching broker inventory: ${url}`);

      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        console.error(`[API] Broker inventory request failed: ${response.status}`, data);
        return { success: false, error: data.error || `HTTP ${response.status}: Failed to get broker inventory data` };
      }

      console.log(`[API] Broker inventory response:`, {
        success: data.success,
        hasData: !!data.data,
        inventoryDataLength: data.data?.inventoryData?.length || 0
      });
      return data;
    } catch (err: any) {
      console.error(`[API] Broker inventory fetch error:`, err);
      return { success: false, error: err.message || 'Failed to get broker inventory data' };
    }
  },

  // Get top brokers for a specific date
  getTopBrokers: async (date: string) => {
    try {
      // Convert YYYY-MM-DD to YYYYMMDD format
      const dateStr = date.includes('-') ? date.replace(/-/g, '') : date;
      const url = `${API_URL}/api/top-broker?date=${dateStr}`;
      console.log(`[API] Fetching top brokers: ${url}`);

      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        console.error(`[API] Top brokers request failed: ${response.status}`, data);
        return { success: false, error: data.error || `HTTP ${response.status}: Failed to get top brokers` };
      }

      console.log(`[API] Top brokers response:`, {
        success: data.success,
        brokersCount: data.data?.brokers?.length || 0
      });
      return data;
    } catch (err: any) {
      console.error(`[API] Top brokers fetch error:`, err);
      return { success: false, error: err.message || 'Failed to get top brokers' };
    }
  },

  // Get available dates for broker transaction data
  async getBrokerTransactionDates(): Promise<{ success: boolean; data?: { dates: string[] }; error?: string }> {
    try {
      // Increase timeout to 60 seconds for Azure Blob Storage operations
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

      const response = await fetch(`${API_URL}/api/broker/transaction/dates`, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[API] getBrokerTransactionDates error response:', response.status, errorText);
        return {
          success: false,
          error: `HTTP ${response.status}: ${errorText || 'Failed to get broker transaction dates'}`
        };
      }

      const data = await response.json();
      console.log('[API] getBrokerTransactionDates success:', data);
      return { success: true, data: data.data };
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.error('[API] getBrokerTransactionDates timeout after 60 seconds');
        return { success: false, error: 'Request timeout - Azure Blob Storage operation took too long' };
      }
      console.error('[API] getBrokerTransactionDates error:', err);
      return { success: false, error: err.message || 'Failed to get broker transaction dates' };
    }
  },

  // Scheduler Logs API
  async getSchedulerLog(logId: string | number): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/trigger/logs/${logId}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to get scheduler log');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get scheduler log' };
    }
  },

  async cancelSchedulerLog(logId: string | number, reason?: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/trigger/logs/${logId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to cancel scheduler log');
      return { success: true, message: data.message };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to cancel scheduler log' };
    }
  },

  async getSchedulerLogs(options?: { limit?: number; offset?: number; status?: string; feature_name?: string }): Promise<{ success: boolean; data?: any[]; total?: number; error?: string }> {
    try {
      const params = new URLSearchParams();
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.offset) params.append('offset', options.offset.toString());
      if (options?.status) params.append('status', options.status);
      if (options?.feature_name) params.append('feature_name', options.feature_name);

      const response = await fetch(`${API_URL}/api/trigger/logs?${params}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to get scheduler logs');
      return { success: true, data: data.data, total: data.total };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get scheduler logs' };
    }
  },

  async triggerBrokerBreakdownData(): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/trigger/broker-breakdown`, {
        method: 'POST',
      }, '/api/trigger/broker-breakdown');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to trigger broker breakdown');
      return { success: true, message: data.message };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to trigger broker breakdown' };
    }
  },

  // Stockbit Config API
  async getStockbitConfig(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/stockbit/config`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to get Stockbit config');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get Stockbit config' };
    }
  },

  async updateStockbitConfig(payload: { enabled?: boolean; interval?: number; token?: string; adminPassword?: string }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/stockbit/config`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update Stockbit config');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to update Stockbit config' };
    }
  },

  async getTradingDataStatus(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/developer/trading-data/status`, {}, '/api/developer/trading-data/status');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to get trading data status');
      return { success: true, data: json.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get trading data status' };
    }
  },

  async startTradingData(): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/developer/trading-data/start`, { method: 'POST' }, '/api/developer/trading-data/start');
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || 'Failed to start trading data');
      }
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to start trading data' };
    }
  },

  async stopTradingData(): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await authenticatedFetch(`${API_URL}/api/developer/trading-data/stop`, { method: 'POST' }, '/api/developer/trading-data/stop');
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error || 'Failed to stop trading data');
      }
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to stop trading data' };
    }
  },

  // Scheduler Management API
  async getSchedulerConfig(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/scheduler/config`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to get scheduler config');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get scheduler config' };
    }
  },

  async updateSchedulerConfig(config: any): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/scheduler/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify(config)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update scheduler config');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to update scheduler config' };
    }
  },

  async stopScheduler(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/scheduler/stop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to stop scheduler');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to stop scheduler' };
    }
  },

  async startScheduler(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/scheduler/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to start scheduler');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to start scheduler' };
    }
  },

  async restartTradingCollector(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/trading-collector/restart`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to restart trading collector');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to restart trading collector' };
    }
  },

  // Stockbit Migration API
  async getMigrationStatus(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/migration/status`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to get migration status');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get migration status' };
    }
  },

  async seedMigration(startDate: string, endDate: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/migration/seed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ startDate, endDate })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to seed migration');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to seed migration' };
    }
  },

  async seedMigrationSmart(startDate: string, endDate: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/migration/seed-smart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ startDate, endDate })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to smart seed migration');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to smart seed migration' };
    }
  },

  async startMigration(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/migration/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to start migration');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to start migration' };
    }
  },

  async stopMigration(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/migration/stop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to stop migration');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to stop migration' };
    }
  },

  async updateMigrationConfig(config: { token?: string; maxWorkers?: number; maxRps?: number }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/migration/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify(config)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update migration config');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to update migration config' };
    }
  },

  async aggregateMigration(date: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/migration/aggregate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ date })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to start aggregation');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to start aggregation' };
    }
  },

  async triggerAggregation(date: string, type: 'IDX' | 'SECTOR'): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/stockbit-migration/aggregation/trigger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ date, type })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Failed to trigger ${type} aggregation`);
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || `Failed to trigger ${type} aggregation` };
    }
  },

  async triggerAggregationRange(startDate: string, endDate: string, type: 'IDX' | 'SECTOR'): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/stockbit-migration/aggregation/trigger-range`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ startDate, endDate, type })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Failed to trigger mass ${type} aggregation`);
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || `Failed to trigger mass ${type} aggregation` };
    }
  },

  async getMigrationAggregationStatus(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/stockbit-migration/aggregation/progress`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to get aggregation status');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get aggregation status' };
    }
  },

  async getMigrationFailedTasks(page: number = 1, limit: number = 50): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/migration/failed?page=${page}&limit=${limit}`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to get failed tasks');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get failed tasks' };
    }
  },

  async getSchedulerStatus(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/scheduler/status`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to get scheduler status');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get scheduler status' };
    }
  },

  async getAllPhasesStatus(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/scheduler/phases`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to get phases status');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get phases status' };
    }
  },

  async triggerPhase(phaseId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/scheduler/phases/${phaseId}/trigger`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to trigger phase');
      return { success: true, message: data.message };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to trigger phase' };
    }
  },

  async togglePhaseEnabled(phaseId: string, enabled: boolean): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/scheduler/phases/${phaseId}/enable`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ enabled })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to toggle phase enabled status');
      return { success: true, message: data.message };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to toggle phase enabled status' };
    }
  },

  async updatePhaseTriggerConfig(
    phaseId: string,
    triggerType: 'scheduled' | 'auto',
    schedule?: string,
    triggerAfterPhase?: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/developer/scheduler/phases/${phaseId}/trigger-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ triggerType, schedule, triggerAfterPhase })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update phase trigger config');
      return { success: true, message: data.message };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to update phase trigger config' };
    }
  },


  async triggerBrokerRange(data: {
    startDate: string;
    endDate: string;
    components?: string | string[];
    force?: boolean;
  }): Promise<{ success: boolean; message?: string; log_id?: string; error?: string }> {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/trigger/broker-range`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      }, '/api/trigger/broker-range');
      
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || result.error || 'Failed to trigger broker range calculation');
      return { success: true, message: result.message, log_id: result.log_id };
    } catch (err: any) {
      console.error('[API] triggerBrokerRange error:', err);
      return { success: false, error: err.message || 'Failed to trigger broker range calculation' };
    }
  },

  // ===== AZURE BLOB TIER MANAGEMENT API =====

  async getArchivedBlobs(prefix: string = 'done-summary/', tier: string = 'all'): Promise<{ success: boolean; data?: any[]; error?: string }> {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/trigger/azure/archived?prefix=${encodeURIComponent(prefix)}&tier=${encodeURIComponent(tier)}`, {}, '/api/trigger/azure/archived');
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || result.error || 'Failed to list archived blobs');
      return { success: true, data: result.data };
    } catch (err: any) {
      console.error('[API] getArchivedBlobs error:', err);
      return { success: false, error: err.message || 'Failed to list archived blobs' };
    }
  },

  async triggerBlobTierChange(payload: { paths: string[]; targetTier: string }): Promise<{ success: boolean; message?: string; log_id?: string; error?: string }> {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/trigger/azure/change-tier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }, '/api/trigger/azure/change-tier');
      
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || result.error || 'Failed to trigger blob tier change');
      return { success: true, message: result.message, log_id: result.log_id };
    } catch (err: any) {
      console.error('[API] triggerBlobTierChange error:', err);
      return { success: false, error: err.message || 'Failed to trigger blob tier change' };
    }
  },

  // Stockbit Stock OHLCV Migration API
  async getStockMigrationStatus(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/stockbit-stock-migration/status`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to get stock migration status');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to get stock migration status' };
    }
  },

  async seedStockMigration(startDate: string, endDate: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/stockbit-stock-migration/seed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ startDate, endDate })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to seed stock migration');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to seed stock migration' };
    }
  },

  async startStockMigration(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/stockbit-stock-migration/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to start stock migration');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to start stock migration' };
    }
  },

  async stopStockMigration(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/stockbit-stock-migration/stop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to stop stock migration');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to stop stock migration' };
    }
  },

  async updateStockMigrationConfig(config: { currentToken?: string; maxWorkers?: number; maxRps?: number }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${API_URL}/api/stockbit-stock-migration/update-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify(config)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update stock migration config');
      return { success: true, data: data.data };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to update stock migration config' };
    }
  },

  // ── Free Trial: Orderbook Replay play count ──────────────────────────────

  async getOrderbookReplayPlayCount(): Promise<{ success: boolean; data?: { count: number; allowed: boolean; isTrial: boolean }; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No active session');
      const response = await authenticatedFetch(
        `${API_URL}/api/orderbook-replay/play-count`,
        { headers: { 'Authorization': `Bearer ${session.access_token}` } },
        '/api/orderbook-replay/play-count'
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to get play count');
      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to get play count' };
    }
  },

  async incrementOrderbookReplayPlay(): Promise<{ success: boolean; data?: { count: number; allowed: boolean; isTrial: boolean }; error?: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No active session');
      const response = await authenticatedFetch(
        `${API_URL}/api/orderbook-replay/increment-play`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        },
        '/api/orderbook-replay/increment-play'
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to increment play count');
      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to increment play count' };
    }
  },

  getTradingCollectorStatus: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No active session');
      const response = await authenticatedFetch(
        `${API_URL}/api/developer/trading-collector/status`,
        {},
        '/api/developer/trading-collector/status'
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to get trading collector status');
      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to get trading collector status' };
    }
  },

  startTradingCollector: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No active session');
      const response = await authenticatedFetch(
        `${API_URL}/api/developer/trading-collector/start`,
        { method: 'POST' },
        '/api/developer/trading-collector/start'
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to start trading collector');
      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to start trading collector' };
    }
  },

  stopTradingCollector: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No active session');
      const response = await authenticatedFetch(
        `${API_URL}/api/developer/trading-collector/stop`,
        { method: 'POST' },
        '/api/developer/trading-collector/stop'
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to stop trading collector');
      return { success: true, data: result.data };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to stop trading collector' };
    }
  },

};
